import { NavLink, Outlet, useParams } from "react-router-dom";
import BoringAvatar from "boring-avatars";
import { AlertCircle } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { AGENT_BRAND_COLORS, Agent, AgentScope, agentPath } from "~/services/agents";
import { StatusBadge } from "./StatusBadge";

const NAV_ITEMS = [
  { label: "Profile", path: "profile" },
  { label: "Skills", path: "skills" },
  { label: "Toolkits", path: "toolkits" },
  { label: "Connections", path: "connections" },
  { label: "Conversations", path: "conversations" },
] as const;

export const AgentDetail = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));

  useNextGenHeader({
    breadcrumbs: [
      { label: "All Agents", href: `${ROUTE_NEXTGEN_VIEW}/agents` },
      ...(agent ? [{ label: agent.display_name }] : []),
    ],
    actions: [
      <Button key="share" variant="outline" size="sm" className="text-xs" disabled>
        Share
      </Button>,
    ],
  });

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border p-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load agent</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-full">
      <nav
        aria-label="Agent sections"
        className="flex w-48 shrink-0 flex-col border-r border-border"
      >
        <div className="flex flex-col items-center gap-2 border-b border-border px-3 py-4">
          <div className="size-20 overflow-hidden rounded-full">
            <BoringAvatar size={80} name={agent.name} variant="beam" colors={AGENT_BRAND_COLORS} />
          </div>
          <p className="text-center text-sm font-medium text-foreground">{agent.display_name}</p>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <StatusBadge status={agent.status} />
            {agent.scope === AgentScope.SYSTEM && (
              <Badge variant="outline" className="rounded-full">
                System
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 p-3">
          {NAV_ITEMS.map(({ label, path }) => (
            <NavLink
              key={path}
              to={path}
              end
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex-1 p-6">
        <Outlet context={agent} />
      </div>
    </div>
  );
};
